import React, { useState, useRef, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import ShipDateDropdown from '../../sharedComponents/ShipDateDropdown';
import useManageWeeklyShip from './useManageWeeklyShip';
import useShipDate from '../setting/shipDate/useShipDate';
import { toast } from 'react-toastify';
import TrackingScanService from '../util/trackingLookup/TrackingScanService';
import WeeklyShipService from './WeeklyShipService';
import { normalizeThaiKeyboard } from '../util/thaiKeyboard';
import { speakThai as speakText } from '../../utils/speakThai';

// QR from print_server.py: `${weight}|${trackingJp}|${orderId}|${boxNo}|${shipDate}`
const parsePipeQR = (value) => {
  if (!value || !value.includes('|')) return null;
  const parts = value.split('|');
  if (parts.length !== 5) return null;
  const [, trackingJp, , boxNo] = parts;
  return { trackingJp: (trackingJp || '').trim() || null, boxNo: (boxNo || '').trim() || null };
};

// The day + slot an air Lalamove customer booked on the platform, e.g. "2026-10-14 15:00".
// Null for other methods and for groups booked without one (sea, admin, older groups).
const lalamoveSlotText = (shipmentGroup) => {
  if (shipmentGroup?.receiveMethod !== 'lalamove' || !shipmentGroup.lalamovePickupDate) return null;
  return [shipmentGroup.lalamovePickupDate, shipmentGroup.lalamovePickupTime].filter(Boolean).join(' ');
};

// Header checkbox component defined outside to avoid React hooks issues with AG Grid
const HeaderCheckboxComponent = (params) => {
  const [checked, setChecked] = useState(false);
  
  useEffect(() => {
    if (!params.api) return;
    
    const updateCheckbox = () => {
      const selectedCount = params.api.getSelectedRows().length;
      const totalCount = params.api.getDisplayedRowCount();
      setChecked(selectedCount === totalCount && totalCount > 0);
    };
    
    params.api.addEventListener('selectionChanged', updateCheckbox);
    updateCheckbox();
    
    return () => {
      params.api.removeEventListener('selectionChanged', updateCheckbox);
    };
  }, [params.api]);
  
  return (
    <div className="flex items-center justify-center h-full">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => {
          if (params.api) {
            if (e.target.checked) {
              params.api.selectAll();
            } else {
              params.api.deselectAll();
            }
          }
        }}
        className="w-4 h-4 cursor-pointer"
        title="เลือกทั้งหมด"
      />
    </div>
  );
};

const WeeklyShipShipmentGroupSummary = () => {
  const [shipDateId, setShipDateId] = useState('');
  const [selectedShipDate, setSelectedShipDate] = useState(null); // Store selected ship date object
  const [summary, setSummary] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCourier, setSelectedCourier] = useState(''); // Empty - must select first
  const [searchText, setSearchText] = useState('');
  const [checkedBoxes, setCheckedBoxes] = useState(new Set()); // boxes verified by scan or manual
  const [scanInput, setScanInput] = useState('');
  const [lastScanResult, setLastScanResult] = useState(null); // 'found' | 'notfound'
  // Delivery groups are packed into ลัง (Air Flow Plan step 10): [{ id, label, weeklyShipIds, exportedAt }]
  const [cartons, setCartons] = useState([]);
  const [currentCartonId, setCurrentCartonId] = useState(null);
  // Lalamove dispatch (step 12): rider tracking link + Lalamove fee
  const [lalamoveLink, setLalamoveLink] = useState('');
  const [lalamoveFee, setLalamoveFee] = useState('');
  const [isSavingLalamove, setIsSavingLalamove] = useState(false);
  // แก้ค่าส่ง: draft fee per ลัง id, filled from the saved fee
  const [cartonFeeDrafts, setCartonFeeDrafts] = useState({});
  const listGridRef = useRef();
  const scanInputRef = useRef();

  const { getShipmentGroupSummaryByShipDateId, exportShipmentGroupToExcel } = useManageWeeklyShip();
  const { shipDates, getShipDates } = useShipDate();

  // Load ship dates on mount
  useEffect(() => {
    getShipDates();
  }, []);

  // When courier changes, auto-deselect rows that don't match
  useEffect(() => {
    if (!selectedCourier || !listGridRef.current?.api) return;
    
    // Get all selected nodes
    const selectedNodes = listGridRef.current.api.getSelectedNodes();
    
    // Deselect rows that don't match the selected courier (only for delivery items)
    selectedNodes.forEach(node => {
      const item = node.data;
      const isDelivery = item?.shipmentGroup?.receiveMethod === 'delivery';
      const courierMatches = item?.shipmentGroup?.courierTh === selectedCourier;
      
      // If it's a delivery item but courier doesn't match, deselect it
      if (isDelivery && !courierMatches) {
        node.setSelected(false);
      }
    });
    
    // Update selectedRows state
    const newSelectedNodes = listGridRef.current.api.getSelectedNodes();
    const newSelectedData = newSelectedNodes.map(node => node.data);
    setSelectedRows(newSelectedData);
  }, [selectedCourier]);

  // Reset scan state and focus input when a new group is selected.
  // Pre-check boxes that are already sentOutAt so the dispatcher only sees what's left.
  useEffect(() => {
    const preChecked = new Set();
    const initialBoxes = selectedGroup?.boxNos || selectedGroup?.boxes || [];
    initialBoxes.forEach((b) => {
      if (typeof b === 'object' && b.sentOutAt) {
        preChecked.add(getBoxLabel(b));
      }
    });
    setCheckedBoxes(preChecked);
    setScanInput('');
    setLastScanResult(null);
    setCartons([]);
    setCurrentCartonId(null);
    setLalamoveLink(selectedGroup?.shipmentGroup?.deliveryStatusUrl || '');
    const savedFee = selectedGroup?.boxes?.[0]?.deliveryFeeTh;
    setLalamoveFee(savedFee != null ? String(Number(savedFee)) : '');
    const groupId = selectedGroup?.shipmentGroup?.id;
    if (groupId && selectedGroup.shipmentGroup.receiveMethod === 'delivery') {
      WeeklyShipService.listCartons(groupId)
        .then(({ data }) => applyCartons(data.cartons))
        .catch(() => toast.error('โหลดข้อมูลลังไม่สำเร็จ'));
    }
    if (selectedGroup) {
      setTimeout(() => scanInputRef.current?.focus(), 100);
    }
  }, [selectedGroup]);

  // For a delivery group "checked" = packed in a ลัง, read from the saved ลัง (survives a refresh).
  const usesCartons = selectedGroup?.shipmentGroup?.receiveMethod === 'delivery';
  const applyCartons = (list, newCurrentId) => {
    const next = list || [];
    setCartons(next);
    setCartonFeeDrafts(Object.fromEntries(next.map((c) => [c.id, c.deliveryFeeTh != null ? String(c.deliveryFeeTh) : ''])));
    setCheckedBoxes(new Set(next.flatMap((c) => c.weeklyShipIds.map((id) => `ws-${id}`))));
    setCurrentCartonId((prev) => {
      const wanted = newCurrentId ?? prev;
      if (wanted && next.some((c) => c.id === wanted)) return wanted;
      return next.length ? next[next.length - 1].id : null;
    });
  };
  const cartonOfBox = (box) => (typeof box === 'object' && box.weeklyShipId
    ? cartons.find((c) => c.weeklyShipIds.includes(box.weeklyShipId)) : null);

  // Mirror sent-out on the left grid rows (the backend sets / clears it with the ลัง).
  const stampSentOut = (weeklyShipIds, sentOutAt) => {
    const ids = new Set(weeklyShipIds);
    const stampBox = (b) => (b && ids.has(b.weeklyShipId) ? { ...b, sentOutAt } : b);
    setSummary((prev) => prev.map((row) => ({
      ...row,
      boxNos: (row.boxNos || []).map(stampBox),
      pendingPreviousBatches: (row.pendingPreviousBatches || []).map((batch) => ({
        ...batch,
        boxes: (batch.boxes || []).map(stampBox),
      })),
    })));
  };

  const packIntoCarton = async (box) => {
    const groupId = selectedGroup?.shipmentGroup?.id;
    if (!groupId || typeof box !== 'object' || !box.weeklyShipId) return;
    try {
      const { data } = await WeeklyShipService.scanIntoCarton(groupId, box.weeklyShipId, currentCartonId);
      applyCartons(data.cartons, data.cartonId);
      const into = data.cartons.find((c) => c.id === data.cartonId)?.label;
      if (data.movedFrom) toast.info(`ย้าย ${getBoxDisplay(box)} จากลัง ${data.movedFrom} → ลัง ${into}`);
      stampSentOut([box.weeklyShipId], new Date().toISOString());
    } catch (err) {
      toast.error(err?.response?.data?.message || 'ใส่ลังไม่สำเร็จ');
    } finally {
      setTimeout(() => scanInputRef.current?.focus(), 0);
    }
  };

  const handleAddCarton = async () => {
    try {
      const { data } = await WeeklyShipService.addCarton(selectedGroup.shipmentGroup.id);
      applyCartons(data.cartons, data.cartonId);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'เพิ่มลังไม่สำเร็จ');
    }
  };

  const handleUnscan = async (weeklyShipId) => {
    try {
      const { data } = await WeeklyShipService.unscanFromCarton(selectedGroup.shipmentGroup.id, weeklyShipId);
      applyCartons(data.cartons);
      stampSentOut([weeklyShipId], null);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'นำออกจากลังไม่สำเร็จ');
    }
  };

  // Save the Lalamove rider link + fee: the group's orders become "ส่งออกแล้ว" and the
  // customer's completed tab shows the link as "เช็คสถานะ". Saving again only updates.
  const handleSaveLalamove = async () => {
    const link = lalamoveLink.trim();
    if (!/^https:\/\/\S+$/i.test(link)) {
      toast.warning('กรุณาวางลิงก์ติดตาม Lalamove (ขึ้นต้นด้วย https://)');
      return;
    }
    if (lalamoveFee === '' || !(Number(lalamoveFee) >= 0)) {
      toast.warning('กรุณากรอกค่าส่ง Lalamove');
      return;
    }
    setIsSavingLalamove(true);
    try {
      const groupId = selectedGroup.shipmentGroup.id;
      const { data } = await WeeklyShipService.dispatchLalamove(groupId, link, Number(lalamoveFee));
      const patch = (row) => (row.shipmentGroup?.id !== groupId ? row : {
        ...row,
        shipmentGroup: { ...row.shipmentGroup, deliveryStatusUrl: data.deliveryStatusUrl },
        boxes: [{ ...(row.boxes?.[0] || {}), deliveryFeeTh: data.fee, status: 'shipped' }],
      });
      setSummary((prev) => prev.map(patch));
      setSelectedGroup((prev) => (prev ? patch(prev) : prev));
      toast.success('บันทึกลิงก์ Lalamove แล้ว — ลูกค้าเห็นสถานะ "ส่งออกแล้ว"');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'บันทึกลิงก์ Lalamove ไม่สำเร็จ');
    } finally {
      setIsSavingLalamove(false);
    }
  };

  // Correct a ลัง's Thai fee instead of re-importing an iShip file. A platform customer's
  // wallet is charged / refunded only the difference.
  const handleSaveCartonFee = async (carton) => {
    const draft = cartonFeeDrafts[carton.id];
    if (draft === '' || draft == null || !(Number(draft) >= 0)) {
      toast.warning('กรุณากรอกค่าส่งให้ถูกต้อง');
      return;
    }
    const fee = Number(Number(draft).toFixed(2));
    const before = carton.deliveryFeeTh != null ? `฿${carton.deliveryFeeTh}` : 'ยังไม่มี';
    if (!window.confirm(
      `เปลี่ยนค่าส่งลัง ${carton.label} จาก ${before} เป็น ฿${fee}?\n` +
      'ลูกค้า platform ที่ถูกตัดเงินแล้ว: ระบบจะตัด/คืนเฉพาะส่วนต่างจาก Wallet'
    )) return;
    try {
      const { data } = await WeeklyShipService.setCartonFee(selectedGroup.shipmentGroup.id, carton.id, fee);
      applyCartons(data.cartons, currentCartonId);
      toast.success(`บันทึกค่าส่งลัง ${carton.label} แล้ว`);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'บันทึกค่าส่งไม่สำเร็จ');
    }
  };

  const handleResetCartons = async () => {
    if (!window.confirm('รีเซ็ตลังทั้งหมดของลูกค้านี้? ทุกกล่องจะกลับเป็น "ยังไม่แพ็ค" แล้วสแกนใหม่ตามที่แพ็คจริง')) return;
    const packed = cartons.flatMap((c) => c.weeklyShipIds);
    try {
      const { data } = await WeeklyShipService.resetCartons(selectedGroup.shipmentGroup.id);
      applyCartons(data.cartons);
      stampSentOut(packed, null);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'รีเซ็ตไม่สำเร็จ');
    }
  };

  // Unique key for checkedBoxes set — weeklyShipId when available, so a box from an
  // older pending batch can't collide with this round's box carrying the same boxNo
  const getBoxLabel = (box) => {
    if (typeof box === 'string' || typeof box === 'number') return String(box);
    if (box.weeklyShipId) return `ws-${box.weeklyShipId}`;
    return String(box.boxNo || box.boxLabel || '');
  };

  // Display text on the pill — boxNo - smileNameInput (+ "ส่งรวม": the customer added this box
  // to the group later, Air Flow Plan step 17 — pack it with the rest)
  const getBoxDisplay = (box) => {
    if (typeof box === 'string' || typeof box === 'number') return String(box);
    const no = box.boxNo ?? box.boxLabel ?? '-';
    const name = box.smileNameInput || '';
    const label = name ? `${no} - ${name}` : String(no);
    return box.joinedAt ? `${label} · ส่งรวม` : label;
  };

  // Find a box from the scanned value. Searches this round's boxes AND the customer's
  // older pending batches. Accepts:
  // - raw trackingJp string  (existing behaviour)
  // - pipe-format QR `weight|trackingJp|orderId|boxNo|shipDate` (from print_server)
  const findBoxByScan = (scannedValue) => {
    const qr = parsePipeQR(scannedValue);
    return allScannableBoxes.find((box) => {
      if (typeof box === 'string' || typeof box === 'number') {
        const s = String(box).toLowerCase();
        if (qr) return (qr.boxNo && s === qr.boxNo.toLowerCase()) || (qr.trackingJp && s === qr.trackingJp.toLowerCase());
        return s === scannedValue.toLowerCase();
      }
      if (qr) {
        if (qr.boxNo && String(box.boxNo || '').toLowerCase() === qr.boxNo.toLowerCase()) return true;
        if (qr.trackingJp && (box.trackingJp || '').toLowerCase() === qr.trackingJp.toLowerCase()) return true;
        return false;
      }
      return box.trackingJp && box.trackingJp.toLowerCase() === scannedValue.toLowerCase();
    });
  };

  const markSentOut = async (weeklyShipId, displayLabel) => {
    if (!weeklyShipId) return;
    try {
      const { data } = await WeeklyShipService.scanOutWeeklyShip(weeklyShipId);
      if (data?.alreadySentOut) {
        const ts = data.sentOutAt ? new Date(data.sentOutAt).toLocaleString('th-TH') : '';
        toast.warning(`${displayLabel} ส่งออกแล้วเมื่อ ${ts}`);
      }
      // Reflect the scan-out in the left grid so the row highlights live.
      const sentOutAt = data?.sentOutAt || new Date().toISOString();
      const stampBox = (b) => (b.weeklyShipId === weeklyShipId ? { ...b, sentOutAt } : b);
      setSummary((prev) => prev.map((row) => ({
        ...row,
        boxNos: (row.boxNos || []).map(stampBox),
        pendingPreviousBatches: (row.pendingPreviousBatches || []).map((batch) => ({
          ...batch,
          boxes: (batch.boxes || []).map(stampBox),
        })),
      })));
    } catch (err) {
      toast.error(err?.response?.data?.message || 'บันทึก sent_out ไม่สำเร็จ');
    } finally {
      // The setSummary above replaces the left grid's rowData. On a group's first
      // scan AG-Grid restores its last focused cell after that refresh, which steals
      // focus off the scan input (the 50ms refocus in handleScanKeyDown already ran,
      // before this async call resolved). Return focus once the refresh has committed.
      setTimeout(() => scanInputRef.current?.focus(), 0);
    }
  };

  const handleScanKeyDown = (e) => {
    if (e.key !== 'Enter') return;
    // Fix scans captured while the PC's input language is Thai (scanner keystrokes
    // become Thai characters); convert back to the English barcode text.
    const value = normalizeThaiKeyboard(scanInput.trim());
    if (!value) return;

    const match = findBoxByScan(value);

    if (match) {
      const key = getBoxLabel(match);
      const newChecked = new Set([...checkedBoxes, key]);
      setCheckedBoxes(newChecked);
      setLastScanResult('found');
      toast.success(`✅ พบกล่อง: ${getBoxDisplay(match)}`);
      const name = (typeof match === 'object' && match.smileNameInput) ? match.smileNameInput : '';
      speakText(name ? name : 'สำเร็จ');
      if (newChecked.size === allScannableBoxes.length) {
        setTimeout(() => speakText('ครบทุกกล่องแล้ว'), 1500);
      }
      TrackingScanService.logShipmentGroupScan({
        trackingJp: value,
        found: true,
        source: 'shipment_group_summary',
        smileNameInput: match.smileNameInput || null,
        shipmentGroupId: selectedGroup?.shipmentGroup?.id || null,
        matchedBoxNo: typeof match === 'object' ? (match.boxNo || null) : String(match),
      }).catch(() => {});
      if (typeof match === 'object' && match.weeklyShipId) {
        if (usesCartons) packIntoCarton(match);
        else markSentOut(match.weeklyShipId, getBoxDisplay(match));
      }
    } else {
      setLastScanResult('notfound');
      toast.error(`❌ ไม่พบ tracking: ${value} ในกลุ่มนี้`);
      speakText('ไม่พบ');
      TrackingScanService.logShipmentGroupScan({
        trackingJp: value,
        found: false,
        source: 'shipment_group_summary',
        shipmentGroupId: selectedGroup?.shipmentGroup?.id || null,
      }).catch(() => {});
    }

    setScanInput('');
    setTimeout(() => scanInputRef.current?.focus(), 50);
  };

  const toggleManualCheck = (label) => {
    // Delivery: a click packs the box into the current ลัง (moves it if it is in another);
    // taking it out is the ✕ on the ลัง card.
    if (usesCartons) {
      const box = allScannableBoxes.find((b) => getBoxLabel(b) === label);
      const inCarton = box && cartonOfBox(box);
      if (box && (!inCarton || inCarton.id !== currentCartonId)) packIntoCarton(box);
      return;
    }
    let nowChecked = false;
    setCheckedBoxes((prev) => {
      const next = new Set(prev);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
        nowChecked = true;
      }
      return next;
    });
    if (nowChecked) {
      const box = allScannableBoxes.find((b) => getBoxLabel(b) === label);
      if (box && typeof box === 'object' && box.weeklyShipId) {
        markSentOut(box.weeklyShipId, getBoxDisplay(box));
      }
    }
  };


  const ExportStatusRenderer = (params) => {
    const isExported = params.data?.shipmentGroup?.exportedAt != null;
    const isSelected = params.node.isSelected();

    return (
      <div className="flex items-center justify-center h-full">
        {/* Custom checkbox with green border */}
        <div
          onClick={(e) => {
            e.stopPropagation();
            params.node.setSelected(!isSelected);
            // Manually trigger selection changed to update selectedRows state
            if (listGridRef.current?.api) {
              const selectedNodes = listGridRef.current.api.getSelectedNodes();
              const selectedData = selectedNodes.map(node => node.data);
              setSelectedRows(selectedData);
            }
          }}
          className="w-5 h-5 rounded cursor-pointer flex items-center justify-center"
          style={{
            border: '2px solid #22c55e',
            backgroundColor: isSelected ? '#22c55e' : 'white'
          }}
          title={isSelected ? 'เลือกแล้ว - พร้อมส่งออก' : 'เลือกสำหรับส่งออก'}
        >
          {isSelected && (
            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          )}
        </div>
      </div>
    );
  };

  // A customer is fully scanned out when every box (this round + held previous
  // batches) has sentOutAt. Held batches always arrive with sentOutAt null, so a
  // customer with unsent held boxes correctly counts as not-done.
  const isGroupScannedOut = (data) => {
    const rowBoxes = [
      ...(data?.boxNos || []),
      ...((data?.pendingPreviousBatches || []).flatMap((b) => b.boxes || [])),
    ];
    return rowBoxes.length > 0 && rowBoxes.every((b) => b.sentOutAt);
  };

  const listColumnDefs = [
    {
      headerName: 'ส่งออกแล้ว',
      field: 'exportedAt',
      width: 140,
      cellRenderer: ExportStatusRenderer,
      headerComponent: 'headerCheckbox'
    },
    {
      headerName: 'ยังไม่จ่าย',
      field: 'notPaidCurrent',
      width: 120,
      // Auto-calculated (display-only): customer still owes on current-or-older orders.
      // The amount breakdown (arrived vs not-arrived) is shown in the right-side detail on click.
      valueGetter: (p) => p.data?.notPaidCurrent ? 'ยังไม่จ่าย' : 'จ่ายแล้ว',
      cellRenderer: (p) => p.data?.notPaidCurrent ? (
        <span className="text-red-600 font-semibold">ยังไม่จ่าย</span>
      ) : (
        <span className="text-green-600">จ่ายแล้ว</span>
      )
    },
    {
      headerName: 'ลูกค้า',
      field: 'customer.contact',
      flex: 1,
      minWidth: 100,
      valueGetter: (p) => p.data?.customer?.contact || '-',
      cellRenderer: (p) => (
        <div className="flex items-center gap-1.5">
          <span>{p.data?.customer?.contact || '-'}</span>
          {p.data?.isCarryOver && (
            <span title="สำเนารายการค้างจากรอบเก่า — คลิกดูรายละเอียดด้านขวา">🔁</span>
          )}
        </div>
      )
    },
    { headerName: 'Smile Name', field: 'customer.smileName', flex: 1, minWidth: 100, valueGetter: (p) => p.data?.customer?.smileName || '-' },
    { headerName: 'ขนส่ง', field: 'shipmentGroup.courierTh', width: 100, valueGetter: (p) => p.data?.shipmentGroup?.courierTh || '-' },
    {
      headerName: 'จำนวนกล่อง',
      field: 'boxCount',
      width: 100,
      valueGetter: (p) => Array.isArray(p.data?.boxNos) ? p.data.boxNos.length : (Array.isArray(p.data?.boxes) ? p.data.boxes.length : 0),
      // Highlight this cell when every box of the customer has been scanned out.
      cellStyle: (p) => isGroupScannedOut(p.data) ? { backgroundColor: '#bbdefb', fontWeight: 600 } : null
    },
    {
      headerName: 'ยอดรวมสินค้า (¥)',
      field: 'totalAmount',
      width: 140,
      type: 'rightAligned',
      valueGetter: (p) => Number(p.data?.totalAmount) || 0,
      valueFormatter: (p) => `¥${(Number(p.value) || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`
    },
    {
      headerName: 'ของค้าง',
      width: 100,
      valueGetter: (p) => (p.data?.pendingPreviousBatches || []).reduce((n, b) => n + (b.boxes?.length || 0), 0),
      cellRenderer: (p) => p.value > 0 ? (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-100 text-orange-700 border border-orange-300">
          ค้าง {p.value}
        </span>
      ) : null
    },
  ];

  const handleShipDateChange = async (e) => {
    const id = e.target.value;
    setShipDateId(id);
    setSelectedGroup(null);
    
    if (!id || id === 'default') {
      setSummary([]);
      setSelectedShipDate(null);
      return;
    }
    
    // Find and store the selected ship date object (handle both string and number ID comparison)
    const foundShipDate = shipDates.find(date => String(date.id) === String(id) || date.id === parseInt(id));
    setSelectedShipDate(foundShipDate || null);
    setIsLoading(true);
    try {
      const response = await getShipmentGroupSummaryByShipDateId(id, true);
      if (response === false || (response && response.success === false)) {
        setSummary([]);
        return;
      }
      // Handle both response.data (from backend) and direct array
      const data = response?.data ? response.data : (Array.isArray(response) ? response : []);
      setSummary(data);
      setSelectedRows([]); // Clear selection when changing ship date
    } catch (err) {
      console.error(err);
      toast.error('โหลดสรุปกลุ่มจัดส่งล้มเหลว');
      setSummary([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRowClicked = (event) => {
    setSelectedGroup(event.data || null);
  };

  const handleSelectionChanged = (event) => {
    const selectedNodes = event.api.getSelectedNodes();
    const selectedData = selectedNodes.map(node => node.data);
    setSelectedRows(selectedData);
  };

  const boxList = (() => {
    if (!selectedGroup) return [];
    if (Array.isArray(selectedGroup.boxNos) && selectedGroup.boxNos.length > 0) return selectedGroup.boxNos;
    if (Array.isArray(selectedGroup.boxes)) return selectedGroup.boxes;
    return [];
  })();

  // Customer's unsent boxes from earlier ship dates (held to send with this batch)
  const oldBatches = selectedGroup?.pendingPreviousBatches || [];
  const allScannableBoxes = [...boxList, ...oldBatches.flatMap((b) => b.boxes || [])];

  const addr = selectedGroup?.address || null;
  const sg = selectedGroup?.shipmentGroup || null;
  const cust = selectedGroup?.customer || null;
  const notPaidArrived = Number(selectedGroup?.notPaidArrivedAmount) || 0;
  const notPaidNotArrived = Number(selectedGroup?.notPaidNotArrivedAmount) || 0;

  // Generate summary text for clipboard (only pickup and lalamove)
  const generateSummaryText = () => {
    if (!summary || summary.length === 0) {
      return 'ไม่มีข้อมูล';
    }

    const grouped = {
      pickup: [],
      lalamove: []
    };

    // Group by receiveMethod (only pickup and lalamove)
    summary.forEach(item => {
      const method = item.shipmentGroup?.receiveMethod || 'unknown';
      const name = item.shipmentGroup?.name || item.customer?.contact || 'ไม่ระบุชื่อ';
      const boxNos = Array.isArray(item.boxNos) && item.boxNos.length > 0
        ? item.boxNos.map(b => (typeof b === 'object' ? String(b.boxNo || b.boxLabel || '') : String(b)))
        : (Array.isArray(item.boxes)
          ? item.boxes.map(b => b.boxLabel || b.boxNo || b)
          : []);
      
      if (method === 'pickup' || method === 'lalamove') {
        grouped[method].push({
          name,
          boxNos: boxNos.join(', '),
          lalamoveSlot: lalamoveSlotText(item.shipmentGroup)
        });
      }
    });

    let text = '';

    // Pickup section
    if (grouped.pickup.length > 0) {
      text += '📦 ไปรับเอง (Pickup):\n';
      grouped.pickup.forEach((item, idx) => {
        text += `${idx + 1}. ${item.name} - กล่อง: ${item.boxNos}\n`;
      });
      text += '\n';
    }

    // Lalamove section
    if (grouped.lalamove.length > 0) {
      text += '🚚 Lalamove:\n';
      grouped.lalamove.forEach((item, idx) => {
        const slot = item.lalamoveSlot ? ` - นัด ${item.lalamoveSlot}` : '';
        text += `${idx + 1}. ${item.name} - กล่อง: ${item.boxNos}${slot}\n`;
      });
      text += '\n';
    }

    // Remove trailing newlines
    return text.trim();
  };

  // Copy summary to clipboard
  const handleCopySummary = async () => {
    try {
      const text = generateSummaryText();
      await navigator.clipboard.writeText(text);
      toast.success('คัดลอกสรุปไปยังคลิปบอร์ดแล้ว');
    } catch (err) {
      console.error('Failed to copy:', err);
      toast.error('ไม่สามารถคัดลอกได้');
    }
  };

  // Get count of exportable items for the selected courier
  const getExportableCount = () => {
    if (!selectedCourier || !selectedRows || selectedRows.length === 0) return 0;
    return selectedRows.filter(item => 
      item.shipmentGroup?.receiveMethod === 'delivery' && 
      item.address &&
      item.shipmentGroup?.courierTh === selectedCourier
    ).length;
  };

  // Export to Excel directly (no modal needed)
  // includeExported = "Export ซ้ำ": also the ลัง that already have an iShip label (after a mistake).
  const handleExportToExcel = async (includeExported = false) => {
    if (includeExported && !window.confirm(
      'Export ซ้ำ: ลังที่ export ไปแล้วจะถูกส่งออกอีกครั้ง\n' +
      'ถ้านำเข้า iShip ซ้ำ จะได้ป้าย/ค่าส่งซ้ำ — ใช้เฉพาะเมื่อ export ครั้งก่อนผิดพลาด\n\n' +
      'นำเข้าไฟล์ iShip ใหม่แล้ว เลขพัสดุ/ค่าส่งของลังจะถูกแทนที่ด้วยของใหม่'
    )) return;
    if (!shipDateId || shipDateId === 'default') {
      toast.warning('กรุณาเลือกวันที่จัดส่งก่อน');
      return;
    }

    if (!selectedCourier) {
      toast.warning('กรุณาเลือกขนส่งก่อน');
      return;
    }

    // Check if any rows are selected
    if (!selectedRows || selectedRows.length === 0) {
      toast.warning('กรุณาเลือกรายการที่ต้องการส่งออก');
      return;
    }

    // Filter to only delivery items for export with matching courier
    const deliveryItems = selectedRows.filter(item => 
      item.shipmentGroup?.receiveMethod === 'delivery' && 
      item.address &&
      item.shipmentGroup?.courierTh === selectedCourier
    );

    if (deliveryItems.length === 0) {
      toast.warning(`ไม่มีรายการที่จัดส่งด้วย ${selectedCourier} ที่สามารถส่งออกได้`);
      return;
    }

    // Get shipment group IDs for selected items (only delivery with matching courier)
    const shipmentGroupIds = deliveryItems
      .map(item => item.shipmentGroup?.id)
      .filter(id => id != null);

    try {
      const success = await exportShipmentGroupToExcel(
        shipDateId,
        shipmentGroupIds,
        selectedCourier,
        [],
        selectedShipDate?.shipDate, // Pass ship date for filename
        includeExported
      );
      if (success === 'nothing-new') {
        toast.info('ไม่มีลังใหม่ให้ export — ทุกลังที่เลือกมีป้าย iShip แล้ว');
      } else if (success) {
        toast.success(`ส่งออกไฟล์ Excel สำเร็จ (${deliveryItems.length} รายการ ${selectedCourier})`);
        // Refresh data to show updated export status
        const refreshedData = await getShipmentGroupSummaryByShipDateId(shipDateId, true);
        if (refreshedData !== false && refreshedData?.success !== false) {
          const data = refreshedData?.data ? refreshedData.data : (Array.isArray(refreshedData) ? refreshedData : []);
          setSummary(data);
          // Clear selection after export
          setSelectedRows([]);
          if (listGridRef.current?.api) {
            listGridRef.current.api.deselectAll();
          }
        }
      } else {
        toast.error('ไม่สามารถส่งออกไฟล์ได้');
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error('เกิดข้อผิดพลาดในการส่งออกไฟล์');
    }
  };

  // Filter the left grid by Smile Name (case-insensitive substring).
  const displayedSummary = searchText.trim()
    ? summary.filter((r) => (r.customer?.smileName || '').toLowerCase().includes(searchText.trim().toLowerCase()))
    : summary;

  return (
    <div className="min-h-screen p-2 sm:p-4 md:p-6 flex flex-col gap-2 sm:gap-4">
      <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0 flex-wrap">
        <div className="w-full sm:w-80">
          <ShipDateDropdown
            handleDataChange={handleShipDateChange}
            includeDefault={true}
            airOnly={true}
          />
        </div>
        {summary.length > 0 && (
          <div className="w-full sm:w-64">
            <input
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="🔍 ค้นหา Smile Name..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:border-blue-400"
            />
          </div>
        )}
        {summary.length > 0 && (
          <div className="flex gap-2 flex-wrap items-center">
            {/* Courier selection dropdown - must select first */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">ขนส่ง:</label>
              <select
                value={selectedCourier}
                onChange={(e) => setSelectedCourier(e.target.value)}
                className={`px-3 py-2 border rounded-lg text-sm ${
                  selectedCourier 
                    ? 'border-green-500 bg-green-50' 
                    : 'border-orange-400 bg-orange-50'
                }`}
              >
                <option value="">-- เลือกขนส่ง --</option>
                <option value="EMS">EMS</option>
                <option value="FLASH">FLASH</option>
                <option value="DHL">DHL</option>
              </select>
            </div>

            {selectedRows.length > 0 && (
              <span className="text-sm text-gray-600">
                เลือกแล้ว {selectedRows.length} รายการ
                {selectedCourier && ` (${getExportableCount()} ${selectedCourier})`}
              </span>
            )}
            <button
              onClick={handleCopySummary}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 text-sm sm:text-base"
              title="คัดลอกสรุปไปยังคลิปบอร์ด"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              คัดลอกสรุป
            </button>
            <button
              onClick={() => handleExportToExcel(true)}
              disabled={!selectedCourier || selectedRows.length === 0 || getExportableCount() === 0}
              className={`px-3 py-2 text-sm rounded-lg border ${
                !selectedCourier || selectedRows.length === 0 || getExportableCount() === 0
                  ? 'border-gray-300 text-gray-400 cursor-not-allowed'
                  : 'border-orange-400 text-orange-700 hover:bg-orange-50'
              }`}
              title="ส่งออกอีกครั้ง รวมลังที่ export ไปแล้ว (ใช้เมื่อครั้งก่อนผิดพลาด)"
            >
              Export ซ้ำ
            </button>
            <button
              onClick={() => handleExportToExcel()}
              disabled={!selectedCourier || selectedRows.length === 0 || getExportableCount() === 0}
              className={`px-4 py-2 flex items-center gap-2 text-sm sm:text-base rounded-lg ${
                !selectedCourier || selectedRows.length === 0 || getExportableCount() === 0
                  ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                  : 'bg-green-600 text-white hover:bg-green-700'
              }`}
              title={
                !selectedCourier 
                  ? 'กรุณาเลือกขนส่งก่อน' 
                  : selectedRows.length === 0 
                    ? 'กรุณาเลือกรายการก่อน' 
                    : getExportableCount() === 0
                      ? `ไม่มีรายการ ${selectedCourier} ที่เลือก`
                      : `ส่งออก ${getExportableCount()} รายการ ${selectedCourier}`
              }
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              {selectedCourier && getExportableCount() > 0 
                ? `Export ${selectedCourier} (${getExportableCount()})` 
                : 'Export Excel'}
            </button>
          </div>
        )}
      </div>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-2 sm:gap-4 lg:gap-6 min-h-0">
        {/* Left: List of shipment groups */}
        <div className="ag-theme-alpine h-[300px] sm:h-[400px] lg:h-full w-full flex-shrink-0 lg:flex-shrink">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">กำลังโหลดสรุป...</div>
          ) : (
            <AgGridReact
              ref={listGridRef}
              rowData={displayedSummary}
              columnDefs={listColumnDefs}
              defaultColDef={{ resizable: true, sortable: true, filter: true }}
              rowSelection="multiple"
              suppressRowClickSelection={true}
              onRowClicked={handleRowClicked}
              onSelectionChanged={handleSelectionChanged}
              components={{
                headerCheckbox: HeaderCheckboxComponent
              }}
              getRowStyle={(params) => {
                if (params.data?.shipmentGroup?.exportedAt) {
                  return { backgroundColor: '#e6f7e6' }; // Light green background
                }
                return null;
              }}
              domLayout="normal"
            />
          )}
        </div>

        {/* Right: Details of the selected group */}
        <div className="w-full overflow-y-auto lg:h-full">
          {!selectedGroup ? (
            <div className="min-h-[200px] flex items-center justify-center text-gray-500 p-4 text-center">
              เลือกกลุ่มจัดส่งทางซ้ายเพื่อดูรายละเอียด
            </div>
          ) : (
            <div className="space-y-2 sm:space-y-4 pb-4">
              {selectedGroup?.isCarryOver && (
                <div className="px-3 py-2 rounded-lg border-2 border-purple-300 bg-purple-50 text-purple-800 text-sm font-medium">
                  🔁 สำเนารายการค้างจากรอบเก่า
                  {selectedGroup?.carryOverShipDate ? ` (${new Date(selectedGroup.carryOverShipDate).toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: '2-digit' })})` : ''}
                  {' '}— ลูกค้าไม่มีกล่องในรอบนี้ เลือกเพื่อส่งออก Excel และสแกนได้ตามปกติ
                </div>
              )}
              <div className="p-3 sm:p-4 bg-white rounded shadow">
                <div className="text-base sm:text-lg md:text-xl font-semibold mb-2 sm:mb-3">รายละเอียดจัดส่ง</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 sm:gap-2 text-xs sm:text-sm">
                  <div className="break-words"><span className="font-medium">ลูกค้า:</span> {cust?.contact || '-'}</div>
                  <div className="break-words"><span className="font-medium">Smile Name:</span> {cust?.smileName || '-'}</div>
                  <div><span className="font-medium">วิธีรับ:</span> {sg?.receiveMethod || '-'}</div>
                  <div><span className="font-medium">ขนส่ง:</span> {sg?.courierTh || '-'}</div>
                  <div><span className="font-medium">จำนวนกล่อง:</span> {Array.isArray(boxList) ? boxList.length : 0}</div>
                  <div><span className="font-medium">ยอดรวมสินค้า:</span> ¥{(Number(selectedGroup?.totalAmount) || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}</div>
                  <div className="col-span-1 md:col-span-2 mt-1 rounded-md border border-gray-200 bg-gray-50 px-2 py-1.5">
                    <div className="font-medium mb-0.5">ยอดค้างจ่าย</div>
                    <div className="flex justify-between">
                      <span>ของมาถึงแล้ว</span>
                      <span className={notPaidArrived > 0 ? 'text-red-600 font-semibold' : 'text-green-600'}>
                        ฿{notPaidArrived.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>ของยังไม่มาถึง</span>
                      <span className={notPaidNotArrived > 0 ? 'text-orange-600 font-semibold' : 'text-gray-500'}>
                        ฿{notPaidNotArrived.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                  {lalamoveSlotText(sg) && (
                    <div className="col-span-1 md:col-span-2">
                      <span className="font-medium">นัด Lalamove:</span> {lalamoveSlotText(sg)}
                    </div>
                  )}
                  {sg?.remark && (
                    <div className="col-span-1 md:col-span-2 break-words">
                      <span className="font-medium">หมายเหตุ:</span> {sg.remark}
                    </div>
                  )}
                </div>
              </div>

              <div className="p-3 sm:p-4 bg-white rounded shadow">
                <div className="text-base md:text-lg font-semibold mb-2 sm:mb-3">ที่อยู่จัดส่ง</div>
                <div className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm">
                  {!addr ? (
                    <div className="text-gray-500">ไม่มีข้อมูลที่อยู่</div>
                  ) : (
                    <>
                      {(() => {
                        const addressParts = [
                          addr.address,
                          addr.subdistrict,
                          addr.district,
                          addr.province,
                          addr.postcode
                        ].filter(part => part && part.trim() !== '');
                        const fullAddress = addressParts.length > 0 
                          ? addressParts.join(', ') 
                          : '-';
                        return (
                          <div className="break-words">
                            <span className="font-medium">ที่อยู่:</span> {fullAddress}
                          </div>
                        );
                      })()}
                      {addr.locationLink && (
                        <div className="break-all">
                          <span className="font-medium">ลิงก์ตำแหน่ง:</span> {addr.locationLink}
                        </div>
                      )}
                    </>
                  )}
                  {/* Always show name and phone, even if address is null */}
                  <div>
                    <span className="font-medium">ชื่อผู้รับ:</span> {addr?.name || sg?.name || cust?.contact || '-'}
                  </div>
                  <div>
                    <span className="font-medium">เบอร์โทร:</span> {addr?.phone || sg?.phone || '-'}
                  </div>
                </div>
              </div>

              <div className="p-3 sm:p-4 bg-white rounded shadow">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-base md:text-lg font-semibold">รายการกล่อง</div>
                  {allScannableBoxes.length > 0 && (
                    <span className={`text-sm font-medium px-2 py-0.5 rounded-full ${
                      checkedBoxes.size === allScannableBoxes.length
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {checkedBoxes.size}/{allScannableBoxes.length} กล่อง
                    </span>
                  )}
                </div>

                {/* Scan input */}
                {allScannableBoxes.length > 0 && (
                  <div className="mb-3">
                    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition-colors ${
                      lastScanResult === 'found'
                        ? 'border-green-400 bg-green-50'
                        : lastScanResult === 'notfound'
                        ? 'border-red-400 bg-red-50'
                        : 'border-indigo-300 bg-indigo-50'
                    }`}>
                      <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m0 14v1M4.22 4.22l.7.7m12.16 12.16.7.7M1 12h1m18 0h1M4.22 19.78l.7-.7M18.36 5.64l.7-.7" />
                      </svg>
                      <input
                        ref={scanInputRef}
                        type="text"
                        value={scanInput}
                        onChange={(e) => { setScanInput(e.target.value); setLastScanResult(null); }}
                        onKeyDown={handleScanKeyDown}
                        placeholder="สแกนบาร์โค้ดกล่อง แล้วกด Enter..."
                        className="flex-1 bg-transparent outline-none text-sm"
                        autoComplete="off"
                        spellCheck={false}
                      />
                    </div>
                    <p className="text-xs text-gray-400 mt-1">หรือคลิกกล่องด้านล่างเพื่อเช็คด้วยตนเอง</p>
                  </div>
                )}

                {boxList.length === 0 ? (
                  <div className="text-sm text-gray-500">ไม่มีรายการกล่อง</div>
                ) : (
                  <div className="max-h-64 overflow-auto">
                    <div className="flex flex-wrap gap-2">
                      {boxList.map((box, idx) => {
                        const label = getBoxLabel(box);
                        const isChecked = checkedBoxes.has(label);
                        return (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => toggleManualCheck(label)}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border-2 transition-all ${
                              isChecked
                                ? 'bg-green-100 border-green-400 text-green-800'
                                : 'bg-blue-50 border-blue-200 text-blue-800 hover:border-blue-400'
                            }`}
                            title={isChecked ? 'คลิกเพื่อยกเลิกเช็ค' : 'คลิกเพื่อเช็คด้วยตนเอง'}
                          >
                            {isChecked && (
                              <svg className="w-3.5 h-3.5 text-green-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            )}
                            {getBoxDisplay(box)}
                            {cartonOfBox(box) && <span className="text-xs opacity-70">· ลัง {cartonOfBox(box).label}</span>}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Older pending batches — boxes the customer held to send with this round */}
                {oldBatches.map((batch) => (
                  <div key={batch.shipDateId} className="mt-3 p-3 rounded-lg border-2 border-amber-300 bg-amber-50">
                    <div className="text-sm font-semibold text-amber-800 mb-2">
                      📦 ค้างจากรอบ {new Date(batch.shipDate).toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: '2-digit' })} ({(batch.boxes || []).length} กล่อง)
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {(batch.boxes || []).map((box, idx) => {
                        const label = getBoxLabel(box);
                        const isChecked = checkedBoxes.has(label);
                        return (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => toggleManualCheck(label)}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border-2 transition-all ${
                              isChecked
                                ? 'bg-green-100 border-green-400 text-green-800'
                                : 'bg-white border-amber-300 text-amber-800 hover:border-amber-500'
                            }`}
                            title={isChecked ? 'คลิกเพื่อยกเลิกเช็ค' : 'คลิกเพื่อเช็คด้วยตนเอง'}
                          >
                            {isChecked && (
                              <svg className="w-3.5 h-3.5 text-green-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            )}
                            {getBoxDisplay(box)}
                            {cartonOfBox(box) && <span className="text-xs opacity-70">· ลัง {cartonOfBox(box).label}</span>}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}

                {/* All checked banner */}
                {allScannableBoxes.length > 0 && checkedBoxes.size === allScannableBoxes.length && (
                  <div className="mt-3 px-3 py-2 bg-green-100 border border-green-300 rounded-lg text-green-700 text-sm font-semibold text-center">
                    ✅ ครบทุกกล่องแล้ว ({allScannableBoxes.length} กล่อง)
                  </div>
                )}

                {/* Lalamove dispatch (Air Flow Plan step 12) — rider link + fee */}
                {sg?.receiveMethod === 'lalamove' && (
                  <div className="mt-4 pt-3 border-t">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-base font-semibold">Lalamove</div>
                      {sg.deliveryStatusUrl && <span className="text-xs font-medium text-green-700">ส่งออกแล้ว</span>}
                    </div>
                    <div className="space-y-2">
                      <input type="text" value={lalamoveLink} onChange={(e) => setLalamoveLink(e.target.value)}
                        placeholder="วางลิงก์ติดตามจากแอป Lalamove (https://...)"
                        className="w-full px-3 py-2 border rounded-lg text-sm" />
                      <div className="flex gap-2">
                        <input type="number" min="0" step="0.01" value={lalamoveFee} onChange={(e) => setLalamoveFee(e.target.value)}
                          placeholder="ค่าส่ง Lalamove (บาท)"
                          className="flex-1 px-3 py-2 border rounded-lg text-sm" />
                        <button type="button" onClick={handleSaveLalamove} disabled={isSavingLalamove}
                          className={`px-4 py-2 text-sm rounded-lg text-white ${isSavingLalamove ? 'bg-gray-400' : 'bg-indigo-600 hover:bg-indigo-700'}`}>
                          {sg.deliveryStatusUrl ? 'แก้ไขลิงก์' : 'ใส่ลิงก์ Lalamove'}
                        </button>
                      </div>
                      <p className="text-xs text-gray-500">บันทึกแล้ว ออเดอร์ของลูกค้าจะเป็น "ส่งออกแล้ว" และลูกค้ากด "เช็คสถานะ" ดูไรเดอร์ได้</p>
                    </div>
                  </div>
                )}

                {/* ลัง (Air Flow Plan step 10) — delivery only; scans go into the current ลัง */}
                {usesCartons && allScannableBoxes.length > 0 && (
                  <div className="mt-4 pt-3 border-t">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-base font-semibold">ลัง (แพ็คจริง)</div>
                      <div className="flex gap-2">
                        <button type="button" onClick={handleAddCarton}
                          className="px-3 py-1 text-sm rounded-lg border border-indigo-400 text-indigo-700 hover:bg-indigo-50">
                          + เพิ่มลัง
                        </button>
                        {cartons.length > 0 && (
                          <button type="button" onClick={handleResetCartons}
                            className="px-3 py-1 text-sm rounded-lg border border-red-300 text-red-600 hover:bg-red-50">
                            รีเซ็ตทั้งหมด
                          </button>
                        )}
                      </div>
                    </div>
                    {cartons.length === 0 ? (
                      <div className="text-sm text-gray-500">ยังไม่มีลัง — สแกนกล่องแรกเพื่อเปิด ลัง 1</div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {cartons.map((carton) => {
                          const isCurrent = carton.id === currentCartonId;
                          const cartonBoxes = carton.weeklyShipIds
                            .map((id) => allScannableBoxes.find((b) => typeof b === 'object' && b.weeklyShipId === id) || { weeklyShipId: id, boxNo: `#${id}` });
                          return (
                            <div key={carton.id} onClick={() => setCurrentCartonId(carton.id)}
                              className={`p-3 rounded-lg border-2 cursor-pointer ${isCurrent ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 bg-white hover:border-indigo-300'}`}>
                              <div className="flex items-center justify-between mb-1">
                                <span className="font-semibold">ลัง {carton.label}</span>
                                <span className="text-xs">
                                  {isCurrent && <span className="text-indigo-700 font-medium">◀ ลังปัจจุบัน</span>}
                                  {carton.exportedAt && <span className="ml-2 text-green-700">export แล้ว</span>}
                                </span>
                              </div>
                              {cartonBoxes.length === 0 ? (
                                <div className="text-xs text-gray-400">ว่าง</div>
                              ) : cartonBoxes.map((b) => (
                                <div key={b.weeklyShipId} className="flex items-center justify-between text-sm py-0.5">
                                  <span>{getBoxDisplay(b)}</span>
                                  <button type="button" title="นำออกจากลัง"
                                    onClick={(e) => { e.stopPropagation(); handleUnscan(b.weeklyShipId); }}
                                    className="text-gray-400 hover:text-red-600 px-1">✕</button>
                                </div>
                              ))}
                              <div className="text-xs text-gray-500 mt-1">
                                {cartonBoxes.length} กล่อง
                                {carton.trackingTh && <span className="ml-2 font-mono text-gray-700">· {carton.trackingTh}</span>}
                              </div>
                              <div className="flex items-center gap-1 mt-2" onClick={(e) => e.stopPropagation()}>
                                <span className="text-xs text-gray-600">ค่าส่ง:</span>
                                <input type="number" min="0" step="0.01"
                                  value={cartonFeeDrafts[carton.id] ?? ''}
                                  onChange={(e) => setCartonFeeDrafts((prev) => ({ ...prev, [carton.id]: e.target.value }))}
                                  className="w-24 px-2 py-0.5 border rounded text-sm" />
                                <button type="button" onClick={() => handleSaveCartonFee(carton)}
                                  disabled={(cartonFeeDrafts[carton.id] ?? '') === (carton.deliveryFeeTh != null ? String(carton.deliveryFeeTh) : '')}
                                  className="px-2 py-0.5 text-xs rounded border border-indigo-400 text-indigo-700 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed">
                                  บันทึก
                                </button>
                                {carton.feeChargedAmount != null && (
                                  <span className="text-xs text-green-700">ตัด Wallet แล้ว ฿{carton.feeChargedAmount}</span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

export default WeeklyShipShipmentGroupSummary;
